import ArrowLeftThin from '@assets/icons/arrow-left-thin.svg';
import BottomSheet, {
  BottomSheetProps,
  BottomSheetScrollView,
} from '@gorhom/bottom-sheet';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AffordanceHandleIndicator } from '@/components/bottom-sheet-backdrop';
import { Analytics } from '@/utils/analytics';
import {
  BackHandler,
  StatusBar,
  useWindowDimensions,
  View,
} from 'react-native';
import {
  BarcodeScanner,
  CAMERA_SCANNER_HEIGHT_MODIFIERS,
  NAVIGATION_HEADER_HEIGHT,
  SCAN_BUTTON_ROW_PADDING,
  SCAN_BUTTON_SIZE,
} from './barcode-scanner';
import { CameraPermissionPrompt } from '@/components/camera-permission-prompt';
import { Code } from '@/react-native-vision-camera';
import { createStyles } from '@/contexts/theme-context';
import { Props as NavigationHeaderProps } from '@/components/navigation-header';
import { OfflineModeBanner } from '@/components/offline-mode-banner';
import { PageContainer } from '@/components/page-container';
import {
  ScanInputField,
  Props as ScanInputFieldProps,
} from './scan-input-field';
import { ScannerSwitch } from './scanner-switch';
import { ScannerType } from './scanner-types';
import { SCREEN_PADDING_SMALL } from '@/styles';
import { useLayoutContext } from '@/contexts/layout-context';
import { useNavigation } from '@react-navigation/native';
import { useRequestCamera } from '@/hooks/use-request-camera';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

export interface Props {
  bottomAction?: React.ReactNode;
  bottomSheetProps?: Omit<BottomSheetProps, 'children'>;
  children: React.ReactNode | ((scannerType: ScannerType) => React.ReactNode);
  errorText?: string | null;
  hideCameraButtons?: boolean;
  hideScanInput?: boolean;
  navigationHeaderProps?:
    | NavigationHeaderProps
    | ((scannerType: ScannerType) => NavigationHeaderProps);
  onClearResults: () => void;
  onScannerTypeChange?: (type: ScannerType) => void;
  scanInputProps?: Partial<ScanInputFieldProps>;
  searchByCode: (code: string, scannerType: ScannerType) => Promise<void>;
}

export const ScanPageContainer: React.FC<Props> = ({
  bottomAction,
  bottomSheetProps,
  children,
  errorText,
  hideCameraButtons = false,
  hideScanInput = false,
  navigationHeaderProps,
  onClearResults,
  onScannerTypeChange,
  scanInputProps,
  searchByCode,
}) => {
  const [styles, theme] = useStyles();
  const { goBack } = useNavigation();
  const { bottom, top } = useSafeAreaInsets();
  const { layoutSize, isSmallLayout } = useLayoutContext();
  const { height: windowHeight } = useWindowDimensions();
  const bottomSheetRef = useRef<BottomSheet>(null);

  const { cameraPermissionStatus, requestCamera } = useRequestCamera();
  const [scannerType, setScannerType] = useState<ScannerType>(
    ScannerType.External,
  );
  const [bottomSheetHeight, setBottomSheetHeight] = useState(0);
  const [bottomActionHeight, setBottomActionHeight] = useState(0);

  useEffect(() => {
    if (!bottomAction) {
      setBottomActionHeight(0);
    }
  }, [bottomAction]);

  const isExternalScanner = scannerType === ScannerType.External;
  const isCameraScanner = scannerType === ScannerType.Camera;

  const changeScannerType = useCallback(() => {
    const nextValue =
      scannerType === ScannerType.External
        ? ScannerType.Camera
        : ScannerType.External;

    Analytics.track('upc_scanner_toggle_button.press', { nextValue });
    setScannerType(nextValue);
    onClearResults();

    onScannerTypeChange?.(nextValue);
  }, [onClearResults, onScannerTypeChange, scannerType]);

  const searchByText = useCallback(
    async (code: string) => {
      await searchByCode(code, ScannerType.External);
    },
    [searchByCode],
  );

  const searchByBarcode = useCallback(
    async (result: Code) => {
      if (result.value?.length) {
        await searchByCode(result.value, ScannerType.Camera);
      }
    },
    [searchByCode],
  );

  const handleBottomSheetClose = useCallback(() => {
    onClearResults();
    bottomSheetRef.current?.snapToIndex(0);
  }, [onClearResults]);

  // Note: useBottomSheetBackHandler currently swallows all back presses if the sheet is visible, which doesn't allow navigating away from scan screen
  useEffect(() => {
    if (!(hideCameraButtons && isCameraScanner)) {
      return;
    }

    const subscription = BackHandler.addEventListener(
      'hardwareBackPress',
      () => {
        handleBottomSheetClose();
        return true;
      },
    );

    return () => subscription.remove();
  }, [hideCameraButtons, isCameraScanner, handleBottomSheetClose]);

  const canUseCamera = cameraPermissionStatus === 'granted';

  const scannerContainerHeight = Math.ceil(
    windowHeight * CAMERA_SCANNER_HEIGHT_MODIFIERS[layoutSize],
  );
  const scannerHeaderHeight = NAVIGATION_HEADER_HEIGHT + top;

  const baseActionSheetHeight =
    isSmallLayout && hideCameraButtons
      ? bottomSheetHeight + SCAN_BUTTON_SIZE + SCAN_BUTTON_ROW_PADDING
      : bottomSheetHeight;

  const snapPoints = [
    baseActionSheetHeight,
    baseActionSheetHeight + scannerContainerHeight - scannerHeaderHeight,
  ];

  const paddingBottom = Math.max(bottom, SCREEN_PADDING_SMALL);
  const statusBarStyle = isCameraScanner ? 'light-content' : 'dark-content';
  const statusBarBackground = isCameraScanner
    ? theme.colors.black
    : theme.colors.white;

  const scanNavigationHeaderProps =
    typeof navigationHeaderProps === 'function'
      ? navigationHeaderProps(scannerType)
      : navigationHeaderProps;

  const scanContent =
    typeof children === 'function' ? children(scannerType) : children;

  return (
    <View style={styles.base} testID={scannerType}>
      <StatusBar
        backgroundColor={statusBarBackground}
        barStyle={statusBarStyle}
      />
      <PageContainer
        containerStyle={{ paddingBottom: bottomActionHeight }}
        navigationHeaderProps={{
          backButtonColor: isExternalScanner
            ? theme.sk.color.icon.primary
            : theme.sk.color.icon.ondark,
          BackButtonIcon: ArrowLeftThin,
          centerTitle: true,
          containerStyle: isCameraScanner
            ? styles.navigationHeaderContainer
            : undefined,
          contentHeader: isSmallLayout ? <OfflineModeBanner /> : undefined,
          onBackPress: goBack,
          showBackButton: true,
          showShadow: false,
          title: (
            <ScannerSwitch
              changeScannerType={changeScannerType}
              scannerType={scannerType}
            />
          ),
          ...scanNavigationHeaderProps,
        }}
      >
        {isExternalScanner ? (
          <>
            {!hideScanInput ? (
              <View style={styles.searchRow}>
                <ScanInputField
                  containerStyle={styles.base}
                  errorTextOverride={errorText}
                  onClear={onClearResults}
                  onSearch={searchByText}
                  {...scanInputProps}
                />
              </View>
            ) : null}
            {scanContent}
          </>
        ) : (
          <View
            style={[styles.base, canUseCamera ? styles.baseActive : null]}
            testID="barcode-scanner-container"
          >
            <BarcodeScanner
              canUseCamera={canUseCamera}
              errorText={errorText}
              onBarcodeScanned={searchByBarcode}
              onClear={onClearResults}
            />
            <View
              onLayout={(e) => {
                const layoutHeight = e.nativeEvent.layout.height;
                if (layoutHeight !== bottomSheetHeight) {
                  setBottomSheetHeight(layoutHeight);
                }
              }}
              style={styles.bottom}
            />
            {baseActionSheetHeight ? (
              <BottomSheet
                animateOnMount={false}
                enableOverDrag={false}
                enablePanDownToClose={hideCameraButtons}
                handleComponent={AffordanceHandleIndicator}
                keyboardBehavior="extend"
                keyboardBlurBehavior="restore"
                onClose={handleBottomSheetClose}
                ref={bottomSheetRef}
                snapPoints={snapPoints}
                style={styles.baseActive}
                {...bottomSheetProps}
              >
                <BottomSheetScrollView alwaysBounceVertical={false}>
                  {canUseCamera ? (
                    <View style={styles.scanResult} testID="scan-result">
                      {scanContent}
                    </View>
                  ) : (
                    <View style={styles.permissionContainer}>
                      <CameraPermissionPrompt
                        cameraPermissionStatus={cameraPermissionStatus}
                        onBackPress={goBack}
                        requestCamera={requestCamera}
                      />
                    </View>
                  )}
                </BottomSheetScrollView>
              </BottomSheet>
            ) : null}
          </View>
        )}
      </PageContainer>
      {bottomAction ? (
        <View
          // Absolutely position bottom action since BottomSheet content height
          // is not static and position can jump when snapping between points
          onLayout={(e) => {
            if (!bottomActionHeight) {
              const layoutHeight = e.nativeEvent.layout.height;
              setBottomActionHeight(layoutHeight);
              const available =
                windowHeight - scannerContainerHeight - layoutHeight;
              setBottomSheetHeight(Math.max(0, available));
            }
          }}
          style={[styles.bottomActionContainer, { paddingBottom }]}
        >
          {bottomAction}
        </View>
      ) : (
        <View
          style={{
            backgroundColor: theme.sk.color.bg.primary.default,
            paddingBottom,
          }}
        />
      )}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    flex: 1,
  },
  baseActive: {
    backgroundColor: theme.colors.overlay,
  },
  bottom: {
    backgroundColor: theme.colors.overlay,
    flex: 1,
  },
  bottomActionContainer: {
    backgroundColor: theme.sk.color.bg.primary.default,
    bottom: 0,
    left: 0,
    position: 'absolute',
    right: 0,
  },
  navigationHeaderContainer: {
    backgroundColor: theme.colors.transparent,
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
    zIndex: 1,
  },
  permissionContainer: {
    flex: 1,
    paddingTop: 40,
  },
  scanResult: {
    flex: 1,
  },
  searchRow: {
    flexDirection: 'row',
    marginHorizontal: SCREEN_PADDING_SMALL,
  },
}));
